import { routes } from "@/router/all_routes";

export interface BreadcrumbItem {
  label: string;
  to?: string;
}

export interface PageMetaOverride {
  pageTitle?: string;
  breadcrumbLabel?: string;
}

const HOME: BreadcrumbItem = { label: "Accueil", to: routes.tenantDashboard };

function trail(...segments: BreadcrumbItem[]): BreadcrumbItem[] {
  if (segments.length === 0) return [{ label: "Accueil" }];
  return [HOME, ...segments];
}

function current(label: string): BreadcrumbItem {
  return { label };
}

function link(label: string, to: string): BreadcrumbItem {
  return { label, to };
}

/** Titre topbar à partir du chemin (+ surcharge page détail). */
export function resolvePageTitle(pathname: string, override?: PageMetaOverride): string {
  if (override?.pageTitle?.trim()) return override.pageTitle.trim();

  const pharmacyOrder = pathname.match(/\/pharmacy\/orders\/([^/]+)$/);
  if (pharmacyOrder) {
    return override?.breadcrumbLabel
      ? `Ordonnance — ${override.breadcrumbLabel}`
      : "Ordonnance";
  }

  const patientDetail = pathname.match(/\/patients\/([^/]+)$/);
  if (patientDetail && !pathname.includes("/records")) {
    return override?.breadcrumbLabel ? `Patient — ${override.breadcrumbLabel}` : "Fiche patient";
  }

  const consultation = pathname.match(/\/clinical\/consultations\/([^/]+)$/);
  if (consultation) {
    return override?.breadcrumbLabel
      ? `Consultation — ${override.breadcrumbLabel}`
      : "Consultation";
  }

  const insuranceCase = pathname.match(/\/insurance\/cases\/([^/]+)$/);
  if (insuranceCase) {
    return override?.breadcrumbLabel ? `Dossier — ${override.breadcrumbLabel}` : "Dossier assurance";
  }

  const insurer = pathname.match(/\/insurance\/insurers\/([^/]+)$/);
  if (insurer) {
    return override?.breadcrumbLabel ?? "Assureur";
  }

  if (pathname.includes("/pharmacy/orders")) return "Pharmacie — ordonnances";
  if (pathname.includes("/pharmacy/invoices")) return "Pharmacie — historique";
  if (pathname.includes("/pharmacy/invoice")) return "Pharmacie — facture";
  if (pathname.includes("/pharmacy/stock")) return "Pharmacie — stock";
  if (pathname.includes("/billing/accounting")) return "Comptabilité";
  if (pathname.includes("/billing/cashier")) return "Caisse";
  if (pathname.includes("/billing/invoices")) return "Recettes & facturation";
  if (pathname.includes("/billing")) return "Facturation";
  if (pathname.includes("/dashboard/finance")) return "Santé financière";
  if (pathname.includes("/dashboard/operations")) return "Performance opérationnelle";
  if (pathname.includes("/services")) return "Services hospitaliers";
  if (pathname.includes("/emergency/transports")) return "Transports ambulance";
  if (pathname.includes("/emergency")) return "Urgences";
  if (pathname.includes("/admissions")) return "Admissions";
  if (pathname.includes("/appointments")) return "Rendez-vous";
  if (pathname.includes("/patients/records")) return "Dossiers médicaux";
  if (pathname.includes("/patients")) return "Patients";
  if (pathname.includes("/clinical/consultations")) return "Consultations";
  if (pathname.includes("/clinical/prescriptions")) return "Prescriptions";
  if (pathname.includes("/clinical/surgery")) return "Chirurgie / bloc";
  if (pathname.includes("/clinical")) return "Clinique";
  if (pathname.includes("/nursing")) return "Soins infirmiers";
  if (pathname.includes("/hospitalization/consumables")) return "Consommables patient";
  if (pathname.includes("/hospitalization")) return "Hospitalisation";
  if (pathname.includes("/laboratory/results")) return "Résultats laboratoire";
  if (pathname.includes("/laboratory")) return "Laboratoire";
  if (pathname.includes("/imaging/reports")) return "Comptes-rendus imagerie";
  if (pathname.includes("/imaging")) return "Imagerie";
  if (pathname.includes("/insurance/partnerships")) return "Partenariats assurance";
  if (pathname.includes("/insurance/cases")) return "Dossiers assurance";
  if (pathname.includes("/insurance/insurers")) return "Assureurs";
  if (pathname.includes("/insurance")) return "Assurance médicale";
  if (pathname.includes("/supply/consumables")) return "Stock consommables";
  if (pathname.includes("/supply/medicines")) return "Stock médicaments";
  if (pathname.includes("/supply")) return "Approvisionnement";
  if (pathname.includes("/rh/")) return "Ressources humaines";
  if (pathname.includes("/admin")) return "Administration";
  if (pathname === routes.tenantDashboard || pathname.endsWith("/tenant/hospitol/")) {
    return "Tableau de bord";
  }
  return "ITaaS Hospitol";
}

/** Fil d'Ariane à partir du chemin (+ libellé feuille optionnel). */
export function buildBreadcrumbTrail(
  pathname: string,
  override?: PageMetaOverride,
): BreadcrumbItem[] {
  const leaf = override?.breadcrumbLabel?.trim();

  if (pathname === routes.tenantDashboard || pathname.endsWith("/tenant/hospitol/")) {
    return [current("Accueil")];
  }

  if (pathname === routes.dashboardFinance) {
    return trail(current("Santé financière"));
  }
  if (pathname === routes.dashboardOperations) {
    return trail(current("Performance opérationnelle"));
  }
  if (pathname === routes.hospitalServicesHub) {
    return trail(current("Services hospitaliers"));
  }
  if (pathname === routes.directionHub) {
    return trail(current("Direction"));
  }

  const pharmacyOrder = pathname.match(/\/pharmacy\/orders\/([^/]+)$/);
  if (pharmacyOrder) {
    return trail(
      link("Pharmacie", routes.pharmacyOrders),
      link("Ordonnances", routes.pharmacyOrders),
      current(leaf ?? "Détail"),
    );
  }

  const patientDetail = pathname.match(/\/patients\/([^/]+)$/);
  if (patientDetail && !pathname.includes("/records")) {
    return trail(link("Patients", routes.patients), current(leaf ?? "Fiche patient"));
  }

  const consultation = pathname.match(/\/clinical\/consultations\/([^/]+)$/);
  if (consultation) {
    return trail(
      link("Clinique", routes.consultations),
      link("Consultations", routes.consultations),
      current(leaf ?? "Détail"),
    );
  }

  const insuranceCase = pathname.match(/\/insurance\/cases\/([^/]+)$/);
  if (insuranceCase) {
    return trail(
      link("Assurance", routes.insuranceHome),
      link("Dossiers", routes.insuranceCases),
      current(leaf ?? "Détail"),
    );
  }

  const insurer = pathname.match(/\/insurance\/insurers\/([^/]+)$/);
  if (insurer) {
    return trail(
      link("Assurance", routes.insuranceHome),
      link("Assureurs", routes.insuranceInsurers),
      current(leaf ?? "Détail"),
    );
  }

  if (pathname.includes("/pharmacy/orders")) {
    return trail(link("Pharmacie", routes.pharmacyOrders), current("Ordonnances"));
  }
  if (pathname.includes("/pharmacy/invoices")) {
    return trail(link("Pharmacie", routes.pharmacyOrders), current("Historique factures"));
  }
  if (pathname.includes("/pharmacy/invoice")) {
    return trail(link("Pharmacie", routes.pharmacyOrders), current("Vente comptoir"));
  }
  if (pathname.includes("/pharmacy/stock/scan")) {
    return trail(link("Pharmacie", routes.pharmacyOrders), link("Stock", routes.pharmacyStockArticles), current("Scan"));
  }
  if (pathname.includes("/pharmacy/stock")) {
    return trail(link("Pharmacie", routes.pharmacyOrders), current("Stock"));
  }
  if (pathname.includes("/billing/accounting")) {
    return trail(link("Facturation", routes.billingInvoices), current("Comptabilité"));
  }
  if (pathname.includes("/billing/cashier")) {
    return trail(link("Facturation", routes.billingInvoices), current("Caisse"));
  }
  if (pathname.includes("/billing/invoices")) {
    return trail(link("Facturation", routes.billingInvoices), current("Recettes"));
  }
  if (pathname.includes("/billing")) {
    return trail(current("Facturation"));
  }
  if (pathname.includes("/emergency/transports")) {
    return trail(link("Urgences", routes.emergency), current("Transports ambulance"));
  }
  if (pathname.includes("/emergency")) return trail(current("Urgences"));
  if (pathname.includes("/admissions")) return trail(current("Admissions"));
  if (pathname.includes("/appointments")) return trail(current("Rendez-vous"));
  if (pathname.includes("/patients/records")) {
    return trail(link("Patients", routes.patients), current("Dossiers médicaux"));
  }
  if (pathname.includes("/patients")) return trail(current("Patients"));
  if (pathname.includes("/clinical/consultations")) {
    return trail(link("Clinique", routes.consultations), current("Consultations"));
  }
  if (pathname.includes("/clinical/prescriptions")) {
    return trail(link("Clinique", routes.consultations), current("Prescriptions"));
  }
  if (pathname.includes("/clinical/surgery")) {
    return trail(link("Clinique", routes.consultations), current("Chirurgie / bloc"));
  }
  if (pathname.includes("/clinical")) return trail(current("Clinique"));
  if (pathname.includes("/nursing")) return trail(current("Soins infirmiers"));
  if (pathname.includes("/hospitalization/consumables")) {
    return trail(
      link("Hospitalisation", routes.hospitalization),
      current("Consommables patient"),
    );
  }
  if (pathname.includes("/hospitalization")) return trail(current("Hospitalisation"));
  if (pathname.includes("/laboratory/results")) {
    return trail(link("Laboratoire", routes.laboratory), current("Résultats"));
  }
  if (pathname.includes("/laboratory")) return trail(current("Laboratoire"));
  if (pathname.includes("/imaging/reports")) {
    return trail(link("Imagerie", routes.imaging), current("Comptes-rendus"));
  }
  if (pathname.includes("/imaging")) return trail(current("Imagerie"));
  if (pathname.includes("/insurance/partnerships")) {
    return trail(link("Assurance", routes.insuranceHome), current("Partenariats"));
  }
  if (pathname.includes("/insurance/cases")) {
    return trail(link("Assurance", routes.insuranceHome), current("Dossiers"));
  }
  if (pathname.includes("/insurance/insurers")) {
    return trail(link("Assurance", routes.insuranceHome), current("Assureurs"));
  }
  if (pathname.includes("/insurance")) return trail(current("Assurance médicale"));
  if (pathname.includes("/supply/consumables/receipts/history")) {
    return trail(
      link("Approvisionnement", routes.supplyConsumablesArticles),
      link("Consommables", routes.supplyConsumablesArticles),
      current("Historique entrées"),
    );
  }
  if (pathname.includes("/supply/medicines/receipts/history")) {
    return trail(
      link("Approvisionnement", routes.supplyMedicinesArticles),
      link("Médicaments", routes.supplyMedicinesArticles),
      current("Historique entrées"),
    );
  }
  if (pathname.includes("/supply/consumables")) {
    return trail(link("Approvisionnement", routes.supplyConsumablesArticles), current("Consommables"));
  }
  if (pathname.includes("/supply/medicines")) {
    return trail(link("Approvisionnement", routes.supplyMedicinesArticles), current("Médicaments"));
  }
  if (pathname.includes("/supply")) return trail(current("Approvisionnement"));
  if (pathname.includes("/rh/employees")) {
    return trail(link("RH", routes.rhEmployees), current("Employés"));
  }
  if (pathname.includes("/rh/departments")) {
    return trail(link("RH", routes.rhEmployees), current("Services"));
  }
  if (pathname.includes("/rh/schedules")) {
    return trail(link("RH", routes.rhEmployees), current("Plannings"));
  }
  if (pathname.includes("/rh/")) return trail(current("Ressources humaines"));
  if (pathname.includes("/admin/users")) {
    return trail(link("Administration", routes.adminSettings), current("Utilisateurs"));
  }
  if (pathname.includes("/admin/settings")) {
    return trail(link("Administration", routes.adminSettings), current("Paramètres"));
  }
  if (pathname.includes("/admin/integrations")) {
    return trail(link("Administration", routes.adminSettings), current("Intégrations"));
  }
  if (pathname.includes("/admin")) return trail(current("Administration"));

  return trail(current("Page"));
}
